import { Marchio, MarchioSimbolo } from '@repo/ui/components/marchio'
import { QueryBreadcrumbs } from '@repo/ui/components/query-breadcrumbs'
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarInset,
	SidebarProvider,
	SidebarTrigger
} from '@repo/ui/sidebar'
import type { ReactNode } from 'react'
import { AdminNavGroups } from './admin-nav-groups'
import { AdminNavUser } from './admin-nav-user'

interface AdminSidebarProps {
	children: ReactNode
}

function AdminSidebar({ children }: AdminSidebarProps) {
	const defaultOpen =
		typeof window !== 'undefined' &&
		window.matchMedia('(min-width: 1280px)').matches

	return (
		<SidebarProvider defaultOpen={defaultOpen}>
			<Sidebar
				className='group-data-[side=left]:border-r-0'
				collapsible='icon'
			>
				<SidebarHeader className='p-1 pt-3'>
					<div className='flex items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center'>
						{/* The brand draws with the page tokens: on the blue frame the symbol turns to a light square with a blue house, the name to light text. */}
						<div className='flex h-10 min-w-10 items-center gap-2.5 px-2 [--foreground:var(--sidebar-foreground)] [--muted-foreground:oklch(from_var(--sidebar-foreground)_l_c_h/0.7)] [--primary-foreground:var(--sidebar)] [--primary:var(--sidebar-foreground)] group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:group-hover:hidden'>
							{/* Collapsed, the symbol stands alone: "Uni" cut from the name reads as a mistake. */}
							<MarchioSimbolo className='size-8' />
							<Marchio className='text-lg group-data-[collapsible=icon]:hidden' />
						</div>
						<SidebarTrigger className='size-10 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:hidden group-data-[collapsible=icon]:group-hover:inline-flex' />
					</div>
				</SidebarHeader>
				<SidebarContent>
					<AdminNavGroups />
				</SidebarContent>
				<SidebarFooter>
					<AdminNavUser />
				</SidebarFooter>
			</Sidebar>
			{/* `overflow-clip`, not `hidden`: a hidden box still scrolls when the browser reveals a focused element or restores a scroll, and takes the whole shell with it. */}
			<SidebarInset className='h-dvh min-w-0 overflow-clip bg-sidebar p-2 md:pl-1'>
				<div className='flex h-full min-h-0 flex-col overflow-clip rounded-xl border border-border bg-background shadow-sm'>
					<header className='flex h-12 shrink-0 items-center gap-2 border-border border-b px-4'>
						<SidebarTrigger className='md:hidden' />
						<QueryBreadcrumbs />
					</header>
					<div className='flex min-h-0 flex-1 flex-col'>
						{children}
					</div>
				</div>
			</SidebarInset>
		</SidebarProvider>
	)
}

export { AdminSidebar }
